import React from "react";
import { Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";

const EMBLEM_URL = "/amber-emblem.png?v=1";

const NAV_LINKS = [
  { label: "The Amber Story", href: "#story" },
  { label: "Vision & Mission", href: "#vision" },
  { label: "Infrastructure", href: "#infrastructure" },
  { label: "Product Mix", href: "#product-mix" },
  { label: "Certifications", href: "#certifications" },
  { label: "Contact", href: "#contact" },
];

const Footer = () => {
  const year = new Date().getFullYear();
  return (
    <footer
      className="relative w-full overflow-hidden"
      style={{ backgroundColor: "#F5EFE4" }}
      data-testid="site-footer"
    >
      {/* Ambient blobs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute -top-24 -left-16 w-96 h-96 rounded-full opacity-40 vm-blob"
          style={{
            background:
              "radial-gradient(circle, rgba(179,100,120,0.18) 0%, rgba(179,100,120,0) 70%)",
          }}
        />
        <div
          className="absolute bottom-0 right-0 w-96 h-96 rounded-full opacity-35 vm-blob-2"
          style={{
            background:
              "radial-gradient(circle, rgba(59,25,48,0.12) 0%, rgba(59,25,48,0) 70%)",
          }}
        />
      </div>

      <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 py-14 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8">
          {/* Brand column */}
          <div className="md:col-span-5">
            <a href="#hero" className="inline-flex items-center gap-3" aria-label="Amber Living">
              <img
                src={EMBLEM_URL}
                alt="Amber Living"
                className="h-12 md:h-14 w-auto"
                draggable={false}
                style={{ objectFit: "contain" }}
              />
              <span
                className="font-semibold tracking-[0.22em] uppercase text-[16px] md:text-[18px]"
                style={{
                  color: "#3B1930",
                  fontFamily: "'Inter', 'Helvetica Neue', system-ui, sans-serif",
                }}
              >
                Amber Living
              </span>
            </a>
            <p
              className="mt-5 max-w-md text-[14.5px] leading-relaxed"
              style={{ color: "#5B4149" }}
            >
              A vertically integrated home textile manufacturer based in Karur,
              India — delivering thoughtfully crafted textile solutions to
              global home brands.
            </p>
          </div>

          {/* Explore column */}
          <div className="md:col-span-3">
            <div
              className="text-[11px] font-semibold tracking-[0.28em] uppercase mb-4"
              style={{ color: "#7A3A52" }}
            >
              Explore
            </div>
            <ul className="space-y-2.5">
              {NAV_LINKS.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="group inline-flex items-center gap-1.5 text-[14px] transition-colors"
                    style={{ color: "#3B1930" }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.color = "#B36478")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.color = "#3B1930")
                    }
                  >
                    <span className="relative">
                      {link.label}
                      <span
                        className="absolute left-0 -bottom-0.5 h-[1.5px] w-0 group-hover:w-full transition-all duration-300 rounded-full"
                        style={{ backgroundColor: "#B36478" }}
                      />
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact column */}
          <div className="md:col-span-4">
            <div
              className="text-[11px] font-semibold tracking-[0.28em] uppercase mb-4"
              style={{ color: "#7A3A52" }}
            >
              Reach Us
            </div>
            <div className="space-y-3.5">
              <a
                href="mailto:raghul@amber-living.in"
                className="group flex items-start gap-3 text-[14px] transition-colors"
                style={{ color: "#3B1930" }}
                data-testid="footer-email"
              >
                <Mail
                  className="w-4 h-4 mt-0.5 shrink-0"
                  strokeWidth={2}
                  style={{ color: "#B36478" }}
                />
                <span className="group-hover:underline underline-offset-4">
                  raghul@amber-living.in
                </span>
              </a>
              <a
                href="tel:+919585956304"
                className="group flex items-start gap-3 text-[14px]"
                style={{ color: "#3B1930" }}
                data-testid="footer-phone"
              >
                <Phone
                  className="w-4 h-4 mt-0.5 shrink-0"
                  strokeWidth={2}
                  style={{ color: "#B36478" }}
                />
                <span className="group-hover:underline underline-offset-4">
                  +91 95859 56304
                </span>
              </a>
              <div
                className="flex items-start gap-3 text-[14px] leading-snug"
                style={{ color: "#5B4149" }}
                data-testid="footer-address"
              >
                <MapPin
                  className="w-4 h-4 mt-0.5 shrink-0"
                  strokeWidth={2}
                  style={{ color: "#B36478" }}
                />
                <span>
                  Plot No. C-28, Puthambur Village
                  <br />
                  Karur Textile Park
                  <br />
                  Karur, Tamil Nadu — 639003
                </span>
              </div>
            </div>

            <a
              href="#contact"
              className="mt-6 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg text-[13px] font-semibold transition-all duration-300 hover:-translate-y-0.5"
              style={{
                color: "#FBE9DA",
                backgroundColor: "#3B1930",
                boxShadow: "0 8px 20px -10px rgba(59,25,48,0.55)",
              }}
              data-testid="footer-cta"
            >
              Start a conversation
              <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2.5} />
            </a>
          </div>
        </div>

        {/* Divider */}
        <div
          className="mt-12 md:mt-14 pt-6 border-t flex flex-col md:flex-row items-start md:items-center justify-between gap-3"
          style={{ borderColor: "rgba(59,25,48,0.12)" }}
        >
          <div
            className="text-[12.5px]"
            style={{ color: "#7A6169" }}
          >
            © {year} Amber Living · Designed in Karur, made for the world.
          </div>
          <div
            className="flex items-center gap-4 text-[12px]"
            style={{ color: "#7A6169" }}
          >
            <span className="uppercase tracking-widest">Vertically Integrated</span>
            <span
              className="inline-block w-1 h-1 rounded-full"
              style={{ backgroundColor: "#B36478" }}
            />
            <span className="uppercase tracking-widest">OEKO-TEX® Certified</span>
            <span
              className="inline-block w-1 h-1 rounded-full"
              style={{ backgroundColor: "#B36478" }}
            />
            <span className="uppercase tracking-widest">Made in India</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
